import React, { useState, useEffect } from 'react';
import { MessageSquare, User, Clock, Search } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ConversationsPage = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Carregar conversas reais do Firestore
  useEffect(() => {
    loadConversations();
  }, [user]);

  const loadConversations = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/business/${user.id}/conversations`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setConversations(data.conversations || []);
        } else {
          console.error('Erro ao carregar conversas:', data.error);
        }
      } else {
        console.error('Erro na requisição de conversas:', response.status);
      }
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Agora há pouco';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h atrás`;
    } else {
      return date.toLocaleDateString('pt-BR');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-secondary-100 text-secondary-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Ativa';
      case 'pending':
        return 'Pendente';
      case 'completed':
        return 'Finalizada';
      default:
        return 'Desconhecido';
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'whatsapp':
        return '📱';
      case 'instagram':
        return '📷';
      case 'facebook':
        return '👥';
      default:
        return '💬';
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.customerPhone.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-secondary-900 font-display">
            Conversas
          </h1>
          <p className="text-secondary-600 mt-1">
            Acompanhe todas as conversas do seu assistente com os clientes
          </p>
        </div>

        {/* Busca */}
        <div className="mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-secondary-400" />
            </div>
            <input
              type="text"
              className="input-field pl-10"
              placeholder="Buscar por nome ou telefone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Lista de Conversas */}
        {filteredConversations.length === 0 ? (
          <div className="card text-center py-12">
            <MessageSquare className="w-16 h-16 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">
              {searchTerm ? 'Nenhuma conversa encontrada' : 'Nenhuma conversa ainda'}
            </h3>
            <p className="text-secondary-600">
              {searchTerm 
                ? 'Tente buscar por outro termo'
                : 'As conversas aparecerão aqui quando os clientes começarem a interagir com seu assistente'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Lista de Conversas */}
            <div className="space-y-4">
              {filteredConversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className={`card-hover cursor-pointer ${
                    selectedConversation?.id === conversation.id 
                      ? 'ring-2 ring-primary-500 border-primary-200' 
                      : ''
                  }`}
                  onClick={() => setSelectedConversation(conversation)}
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-primary-100 p-2 rounded-full flex-shrink-0">
                      <User className="w-5 h-5 text-primary-600" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-sm font-semibold text-secondary-900 truncate">
                          {conversation.customerName}
                        </h3>
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{getPlatformIcon(conversation.platform)}</span>
                          <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(conversation.status)}`}>
                            {getStatusText(conversation.status)}
                          </span>
                        </div>
                      </div>
                      
                      <p className="text-sm text-secondary-600 truncate mb-2">
                        {conversation.lastMessage}
                      </p>
                      
                      <div className="flex items-center justify-between text-xs text-secondary-500">
                        <span>{conversation.customerPhone}</span>
                        <div className="flex items-center space-x-2">
                          <span>{conversation.messageCount} mensagens</span>
                          <span>•</span>
                          <div className="flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>{formatTime(conversation.lastMessageAt)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Detalhes da Conversa */}
            <div className="card">
              {selectedConversation ? (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-secondary-900">
                        {selectedConversation.customerName}
                      </h3>
                      <p className="text-sm text-secondary-600">
                        {selectedConversation.customerPhone}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{getPlatformIcon(selectedConversation.platform)}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(selectedConversation.status)}`}>
                        {getStatusText(selectedConversation.status)}
                      </span>
                    </div>
                  </div>

                  {/* Mensagens Simuladas */}
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-start">
                      <div className="bg-secondary-100 rounded-lg p-3 max-w-xs">
                        <p className="text-sm text-secondary-900">
                          Olá! Gostaria de saber mais sobre o Smartphone Galaxy Pro.
                        </p>
                        <p className="text-xs text-secondary-500 mt-1">10:25</p>
                      </div>
                    </div>
                    
                    <div className="flex justify-end">
                      <div className="bg-primary-500 text-white rounded-lg p-3 max-w-xs">
                        <p className="text-sm">
                          Olá! Claro, o Galaxy Pro é nosso smartphone top de linha com câmera de 108MP. Custa R$ 2.499,99. Gostaria de mais detalhes?
                        </p>
                        <p className="text-xs text-primary-200 mt-1">10:26</p>
                      </div>
                    </div>
                    
                    <div className="flex justify-start">
                      <div className="bg-secondary-100 rounded-lg p-3 max-w-xs">
                        <p className="text-sm text-secondary-900">
                          Sim! Qual é a capacidade de armazenamento?
                        </p>
                        <p className="text-xs text-secondary-500 mt-1">10:27</p>
                      </div>
                    </div>
                    
                    <div className="flex justify-end">
                      <div className="bg-primary-500 text-white rounded-lg p-3 max-w-xs">
                        <p className="text-sm">
                          O Galaxy Pro vem com 256GB de armazenamento interno e suporte para cartão microSD de até 1TB. Também tem 12GB de RAM.
                        </p>
                        <p className="text-xs text-primary-200 mt-1">10:28</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-secondary-200 pt-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-secondary-600">Iniciada em:</span>
                        <p className="font-medium text-secondary-900">
                          {new Date(selectedConversation.lastMessageAt).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      <div>
                        <span className="text-secondary-600">Total de mensagens:</span>
                        <p className="font-medium text-secondary-900">
                          {selectedConversation.messageCount}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <MessageSquare className="w-16 h-16 text-secondary-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-secondary-900 mb-2">
                    Selecione uma conversa
                  </h3>
                  <p className="text-secondary-600">
                    Clique em uma conversa da lista para ver os detalhes
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ConversationsPage;

